import { Star, Sparkles, Leaf, Circle, Diamond, Square, Triangle, Heart } from "lucide-react";
import type { ColorScheme } from "./types";

export const LOGO_ICONS = [
  { icon: Star, label: "Star", keywords: "star shine light favorite" },
  { icon: Sparkles, label: "Sparkles", keywords: "sparkle magic shine glitter" },
  { icon: Leaf, label: "Leaf", keywords: "leaf nature plant eco green" },
  { icon: Circle, label: "Circle", keywords: "circle round dot" },
  { icon: Diamond, label: "Diamond", keywords: "diamond gem jewel premium" },
  { icon: Square, label: "Square", keywords: "square box block" },
  { icon: Triangle, label: "Triangle", keywords: "triangle pyramid delta" },
  { icon: Heart, label: "Heart", keywords: "heart love like favorite" },
];

export const colorSchemes: Record<string, ColorScheme> = {
  slateLight: {
    name: "Slate Light",
    primary: "#64748b",
    secondary: "#f1f5f9",
    accent: "#475569",
    glass: "rgba(241, 245, 249, 0.8)",
    light: "#ffffff",
    dark: "#1e293b"
  },
  slateDark: {
    name: "Slate Dark",
    primary: "#94a3b8",
    secondary: "#1e293b",
    accent: "#cbd5e1",
    glass: "rgba(30, 41, 59, 0.8)",
    light: "#f8fafc",
    dark: "#0f172a"
  }
};

export const proColorSchemes: Record<string, ColorScheme> = {
  slateSky: {
    name: "Slate Sky",
    primary: "#5a88a6",
    secondary: "#f1f7fa",
    accent: "#466b87",
    glass: "rgba(241, 247, 250, 0.8)",
    light: "#ffffff",
    dark: "#2e414e"
  },
  blushPastel: {
    name: "Blush Pastel",
    primary: "#e4b7b7",
    secondary: "#fdf2f2",
    accent: "#dca5a5",
    glass: "rgba(253, 242, 242, 0.8)",
    light: "#ffffff",
    dark: "#4a3434"
  },
  tealDream: {
    name: "Teal Dream",
    primary: "#319795",
    secondary: "#e6f7f6",
    accent: "#276b68",
    glass: "rgba(230, 247, 246, 0.8)",
    light: "#ffffff",
    dark: "#223433"
  },
  berryBold: {
    name: "Berry Bold",
    primary: "#b63e82",
    secondary: "#fdeef4",
    accent: "#8e3463",
    glass: "rgba(253, 238, 244, 0.8)",
    light: "#ffffff",
    dark: "#2e1c2b"
  },
  bubblegumBliss: {
    name: "Bubblegum Bliss",
    primary: "#ff6fcb",
    secondary: "#ffe8f6",
    accent: "#e05fae",
    glass: "rgba(255, 232, 246, 0.8)",
    light: "#ffffff",
    dark: "#4a2c3c"
  },
  softPearl: {
    name: "Soft Pearl",
    primary: "#5b8dfc",
    secondary: "#f6f9ff",
    accent: "#325ac7",
    glass: "rgba(246, 249, 255, 0.8)",
    light: "#ffffff",
    dark: "#23344d"
  },
  cloudMist: {
    name: "Cloud Mist",
    primary: "#7db89f",
    secondary: "#f8faf9",
    accent: "#4f7e68",
    glass: "rgba(248, 250, 249, 0.8)",
    light: "#ffffff",
    dark: "#2a3b34"
  },
  blushTint: {
    name: "Blush Tint",
    primary: "#e37878",
    secondary: "#fff8f8",
    accent: "#c04e4e",
    glass: "rgba(255, 248, 248, 0.8)",
    light: "#ffffff",
    dark: "#402c2c"
  },
  powderBlue: {
    name: "Powder Blue",
    primary: "#589bd6",
    secondary: "#f1f8fe",
    accent: "#3472b3",
    glass: "rgba(241, 248, 254, 0.8)",
    light: "#ffffff",
    dark: "#24374a"
  },
  mintWhisper: {
    name: "Mint Whisper",
    primary: "#5ecfa3",
    secondary: "#f5fcf8",
    accent: "#36987a",
    glass: "rgba(245, 252, 248, 0.8)",
    light: "#ffffff",
    dark: "#2b4f43"
  },
  lavenderDream: {
    name: "Lavender Dream",
    primary: "#9f82fc",
    secondary: "#f9f8fe",
    accent: "#6c5cc8",
    glass: "rgba(249, 248, 254, 0.8)",
    light: "#ffffff",
    dark: "#322b50"
  },
  goldenHaze: {
    name: "Golden Haze",
    primary: "#f4ab42",
    secondary: "#fffaf4",
    accent: "#d38a1b",
    glass: "rgba(255, 250, 244, 0.8)",
    light: "#ffffff",
    dark: "#4a361b"
  }
};

export const defaultFunnelData = {
  siteName: "My Funnel",
  siteTitle: "Welcome",
  logo: "",
  favicon: "",
  headerImage: "",
  theme: "modern",
  showBadge: true,
  pages: [{
    id: crypto.randomUUID(),
    title: "Main Page",
    slug: "",
    sections: [],
    showInMenu: true,
    showHeader: true,
    showFooter: true,
    isMainPage: true,
    order: 0
  }],
  sections: [], // For backward compatibility
  formElements: [],
  afterSubmit: {
    title: "Thank You!",
    text: "We'll be in touch soon.",
    buttonText: "Return Home",
    buttonUrl: "/"
  }
};

export const buttonThemes = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "primary", label: "Primary" },
  { value: "secondary", label: "Secondary" }
] as const;

export const buttonTargets = [
  { value: "self", label: "Same Tab" },
  { value: "new", label: "New Tab" }
] as const;
